import { useState, useRef } from 'react';
import { Upload, Download, FileJson, Check, AlertCircle } from 'lucide-react';
import { issuesData, cardsData, decisionTreeData, maintenanceData } from '../content';

type DataFile = 'issues' | 'cards' | 'decisionTree' | 'maintenance';

const fileData: Record<DataFile, object> = {
  issues: issuesData,
  cards: cardsData,
  decisionTree: decisionTreeData,
  maintenance: maintenanceData
};

export function Admin() {
  const [selectedFile, setSelectedFile] = useState<DataFile>('issues');
  const [jsonContent, setJsonContent] = useState(() => 
    JSON.stringify(fileData.issues, null, 2)
  );
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (file: DataFile) => {
    setSelectedFile(file);
    setJsonContent(JSON.stringify(fileData[file], null, 2));
    setIsValid(true);
    setErrorMessage('');
  };

  const validateJson = (content: string): boolean => {
    try {
      JSON.parse(content);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleContentChange = (content: string) => {
    setJsonContent(content);
    const valid = validateJson(content);
    setIsValid(valid);
    if (!valid) {
      setErrorMessage('Invalid JSON syntax');
    } else {
      setErrorMessage('');
    }
  };

  const handleDownload = () => {
    if (!isValid) return;
    
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFile}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setJsonContent(content);
      const valid = validateJson(content);
      setIsValid(valid);
      if (valid) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } else {
        setErrorMessage('Uploaded file contains invalid JSON');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Content Editor</h1>
        <p className="text-dtf-text-muted">
          Edit JSON content files locally. Download your changes and replace files in the /content folder.
        </p>
      </div>

      {/* File Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {(Object.keys(fileData) as DataFile[]).map((file) => (
          <button
            key={file}
            onClick={() => handleFileChange(file)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedFile === file
                ? 'bg-dtf-accent text-dtf-dark'
                : 'bg-dtf-panel text-dtf-text-muted hover:text-white'
            }`}
          >
            <FileJson className="w-4 h-4" />
            {file}.json
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={handleDownload}
          disabled={!isValid}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            isValid
              ? 'bg-dtf-accent text-dtf-dark hover:bg-dtf-accent-hover'
              : 'bg-dtf-panel text-dtf-gray cursor-not-allowed'
          }`}
        >
          <Download className="w-4 h-4" />
          Download
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 bg-dtf-panel border border-dtf-border text-white rounded-lg hover:border-dtf-accent transition-colors"
        >
          <Upload className="w-4 h-4" />
          Upload JSON
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleUpload}
          className="hidden"
        />

        {showSuccess && (
          <div className="flex items-center gap-2 text-dtf-green">
            <Check className="w-4 h-4" />
            <span className="text-sm">Success!</span>
          </div>
        )}
      </div>

      {/* Validation Status */}
      {!isValid && (
        <div className="flex items-center gap-2 text-red-400 mb-4">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{errorMessage}</span>
        </div>
      )}

      {/* JSON Editor */}
      <div className="bg-dtf-panel border border-dtf-border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-dtf-dark border-b border-dtf-border flex items-center justify-between">
          <span className="text-sm text-dtf-text-muted font-mono">{selectedFile}.json</span>
          <span className={`text-xs px-2 py-0.5 rounded ${
            isValid ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {isValid ? 'Valid JSON' : 'Invalid JSON'}
          </span>
        </div>
        <textarea
          value={jsonContent}
          onChange={(e) => handleContentChange(e.target.value)}
          className="w-full h-96 p-4 bg-dtf-dark text-dtf-text font-mono text-sm resize-none focus:outline-none"
          spellCheck={false}
        />
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-dtf-panel border border-dtf-border rounded-xl p-4">
        <h3 className="font-semibold text-white mb-2">How to Use</h3>
        <ol className="text-dtf-text-muted text-sm space-y-2 list-decimal list-inside">
          <li>Edit the JSON content in the editor above</li>
          <li>Click "Download" to save your changes</li>
          <li>Replace the corresponding file in the /content folder</li>
          <li>Refresh the app to see your changes</li>
        </ol>
        <p className="text-dtf-gray text-sm mt-4">
          Note: Changes made here are not persisted to the server. Always download and save your changes.
        </p>
      </div>
    </div>
  );
}
